<template>
  <div v-if="listView && entries">
    <list-view-table-meta-links
      :total-count="totalCount"
      :show-view-all="hasMore"
      :showCreateRelatedEntry="showCreateButton"
      @click-view-all="clickViewAll"
      @click-create-related-entry="goToCreateRelatedEntry"
      :show-connected-text="true"
    ></list-view-table-meta-links>
    <list-view-scrollable-table
      :is-in-detail-view-edit-mode="isInEditMode"
      is-embedded
      :list-view="listView"
      :entries="entriesArray"
    ></list-view-scrollable-table>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
import { RouteHelper } from "~/helpers/routeHelper";
import { Route } from "~/enums/route";
import { DetailViewContentElementCapability } from "~/models/detailView/elements/detailViewContentElementCapability";
import ObjectModelFakeEntry from "~/models/objectModelEntries/objectModelFakeEntry";
import ListViewScrollableTable from "./list-view-scrollable-table.vue";
import ListViewTableMetaLinks from "./list-view-table-meta-links.vue";
import { Area } from "../enums/area";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import {ObjectModelRelationType} from "~/enums/objectModelRelationType";
import ContentElementComponent from "./content-element-component.vue";
import breadcrumbMixin from "../mixins/breadcrumbMixin";
import ObjectModelAction from "~/models/objectModelActions/objectModelAction";
import {ObjectModelDefaultActionType} from "~/enums/objectModelDefaultActionType";
import PublishedListView from "~/models/listViews/publishedListView";
import RefreshEntryDataBus from '../buses/refreshEntryDataBus';

export default Vue.extend({
  extends: ContentElementComponent,
  name: "detail-view-content-element-related-object",
  mixins: [
    breadcrumbMixin,
  ],
  components: {
    ListViewScrollableTable,
    ListViewTableMetaLinks
  },
  props: {
    objectModelEntry: {
      type: ObjectModelEntry
    }
  },
  data: function() {
    return {
      DetailViewContentElementCapability,
      listView: null,
      entries: null,
      relation: null,
      totalCount: null,
    };
  },
  beforeMount() {
    this.registerEventBusListeners();
    this.fetchAssociatedListViewAndEntries();
  },
  computed: {
    listViewLink(): string {
      const routeEnum = this.isPreview ? Route.previewListView : Route.listView;
      const query = {}

      if (this.currentBreadcrumbs) {
        query['bc'] = [
          ...this.currentBreadcrumbs,
          {t: 't', v: this.contentElement.label_title},
        ];
      } else {
        query['bc'] = [
          {t: 'om', v: 'bv'},
          {t: 'lv', v: this.$route.query["from_list_view"]},
          {t: 'dv', v: {dv: this.selectedDetailView.detail_view_id, e: this.selectedObjectModelEntry.id}},
          {t: 't', v: this.contentElement.label_title},
        ];
      }

      query['relationId'] = this.contentElement.related_object_id
      query['parentEntryId'] = this.selectedObjectModelEntry.id
      query['detailViewId'] = this.selectedDetailView.detail_view_id;
      query['dvContentElementId'] = this.contentElement.client_id
      return RouteHelper.path({ route: routeEnum, modelOrId: this.listView.list_view_id || this.listView.id, query });
    },
    entriesLength() {
      return this.entries.length;
    },
    entriesArray(): Array<any> {
      return this.entries.slice(0, this.maxDisplay);
    },
    hasMore(): boolean {
      if (this.relationIsSingle) return false;
      return this.entries && this.entriesLength > this.maxDisplay;
    },
    maxDisplay(): number {
      return this.contentElement.record_display_limit || 0;
    },
    newRelatedEntryPath(): string {
      const namedWildcardOptions = RouteHelper.namedWildcardOptionsFactory({object_model: this.listView.object_model_id});
      return RouteHelper.path({
        route: Route.objectModelEntriesNew,
        namedWildcardOptions,
        query: {
          ...this.$router.currentRoute.query,
          from_detail_view: this.contentElement.rootModel.detail_view_id,
          from_entry: this.selectedObjectModelEntry.id,
          for_relation: this.contentElement.related_object_id,
          inverse: !this.contentElement.inverse
        }
      });
    },
    showCreateButton(): boolean {
      if (!this.listView || !this.createObjectModelAction) return false;
      if (!this.$can('enableMainAction', this.listView, {action: this.createObjectModelAction})) return false;
      if (!this.$can('connectOrSever', this.relation, { objectModel: this.selectedDetailView.published_object_model })) return false

      // if relation type is OneToMany inverse or OneToOne, and there are already entries, then hide create button
      if ((this.relation?.relation_type === ObjectModelRelationType.OneToMany) && (this.relation.object_model_id !== this.selectedObjectModelEntry.object_model_id)) {
        if (this.entriesArray.length > 0) return false
      } else if (this.relation?.relation_type === ObjectModelRelationType.OneToOne) {
        if (this.entriesArray.length > 0) return false
      }
      return true
    },
    createObjectModelAction(): ObjectModelAction | null {
      const om = this.listView.published_object_model || this.listView.object_model;
      return om.objectModelDefaultActionsArray.find(
        (a) => a.default_action_type === ObjectModelDefaultActionType.Create
      );
    },
  },
  methods: {
    registerEventBusListeners(): void {
      RefreshEntryDataBus.onRefresh(() => {
        if (this.isPreview) {
          this.fetchAssociatedFakeEntries();
        } else {
          this.fetchAssociatedEntries();
        }
      });
    },
    fetchAssociatedListViewAndEntries(): void {
      this.$store
        .dispatch("detailViewStore/fetchAssociatedListView", {
          organizationId: this.selectedOrganization.id,
          detailViewId: this.isPreview ? this.selectedDetailView.id : this.selectedDetailView.detail_view_id,
          listViewId: this.contentElement.list_view_id,
          entryId: this.selectedObjectModelEntry.id,
        })
        .then(listView => {
          this.listView = listView;
          if (this.isPreview) {
            this.fetchAssociatedFakeEntries();
          } else {
            this.fetchAssociatedEntries();
          }
        });
    },
    fetchAssociatedFakeEntries(): void {
      this.$store.dispatch("detailViewStore/fetchAssociatedFakeEntries", { listView: this.listView }).then(results => {
        this.entries = results.entries.map(entryJson => {
          const newEntry = ObjectModelFakeEntry.repo.find(entryJson.id) || new ObjectModelFakeEntry();
          return newEntry.loadFromJson(entryJson);
        });
        this.totalCount = results.totalCount
      });
    },
    async fetchAssociatedEntries() {      
      const urlParams = new URLSearchParams(window.location.search);
      const highlightedEntryId = urlParams.get('highlighted_entry_id');

      const [entries, relation, totalCount] = await this.$store.dispatch("detailViewStore/fetchAssociatedEntries", {
        listView: this.listView, 
        limit: this.maxDisplay, 
        relationId: this.contentElement.related_object_id,
        detailViewId: this.isPreview ? this.selectedDetailView.id : this.selectedDetailView.detail_view_id,
        parentEntry: this.selectedObjectModelEntry,
        inverse: this.contentElement.inverse,
        highlightedEntryId: highlightedEntryId
      });
      
      this.relation = ObjectModelRelation.repo.findOrCreate(relation.id, relation.client_id).loadFromJson(relation)
      this.totalCount = totalCount

      this.entries = entries.map(entryJson => {
        const newEntry = ObjectModelEntry.repo.find(entryJson.id) || new ObjectModelEntry();
        if (ObjectModelEntry.repo.find(entryJson.id)) {
          // merge available action client ids
          // new data comes afterwards to overwrite in case of duplicate keys
          entryJson.view_data.available_action_client_ids = {
            // old entry data
            ...newEntry.view_data?.available_action_client_ids,
            // new entry data
            ...entryJson.view_data?.available_action_client_ids
          }
        }
        return newEntry.loadFromJson(entryJson);
      });
    },
    viewMore(): void {
      if (this.isPreview) {
        this.$router.push({
          name: Area.previewDetailViewListView,
          params: { detail_view_id: this.selectedDetailView.id, list_view_id: this.listView.id }
        });
      } else {
        this.$router.push({
          name: Area.listView,
          params: { id: this.listView.id, detail_view_id: this.selectedDetailView.id }
        });
      }
    },
    goToCreateRelatedEntry(): void {
      window.location.href = this.newRelatedEntryPath;
    },
    clickViewAll(): void {
      window.location.href = this.listViewLink
    }
  }
});
</script>